import { useI18n } from "vue-i18n";
import type { MaybeRefOrGetter } from "vue";
import type { BrandConfig } from "@dashboard/brands/brandConfig";
import { useDashboardSession } from "./useDashboardSession";
import { useDashboardView } from "./useDashboardView";

/**
 * Brand-root facade: starts the dashboard session and exposes store projections.
 * Child components should use useDashboardView instead of calling this composable.
 * @param brandConfig - Brand settings, supplied directly or through a reactive ref or getter.
 */
export function useCoreServices(brandConfig: MaybeRefOrGetter<BrandConfig>) {
  const { t } = useI18n();
  const session = useDashboardSession(brandConfig);
  const view = useDashboardView();

  /**
   * Returns a translation, falling back to the supplied text when the key is unresolved.
   * @remarks Migration: Uses useI18n instead of this.$t, preserving the original fallback rule.
   */
  function getTranslatedText(byDefault: string, translate: string): string {
    let result = byDefault;
    if (t(translate) !== translate) {
      result = t(translate);
    }

    return result;
  }

  return {
    ...view,
    ...session,
    getTranslatedText,
  };
}